import { Component, computed, effect, inject, Input, ViewChild, WritableSignal } from '@angular/core';
import { RecordService } from '../../record/record.service';
import { MatTableDataSource } from '@angular/material/table';
import { IRptDtxClm, IRptDtxDet, IRptDtxParm, IRptDtxPay, IRptDtxTot, IRptDtxView } from '../../shared/interfaces/rptDtx';
import { DataMessageService } from '../../shared/data-message.service';
import { AppToastsService } from '../../shared/app-toasts/app-toasts.service';
import { ExportExcelService } from '../../shared/export-excel.service';
import { MatDialog } from '@angular/material/dialog';
import { IRptDtx } from '../../shared/interfaces/rptDtx';
import { MatSort } from '@angular/material/sort';
import moment from 'moment';
import { CkRegisterService } from '../../ck-register/ck-register.service';
import { ModalService } from '../../shared/modal.service';
import { take } from 'rxjs';

interface IExpandedCol {
  index: number;
  show: boolean;
  details: IRptDtxDet[];
  payments: IRptDtxPay[];
}

@Component({
  selector: 'app-rpt-dtx',
  imports: [],
  templateUrl: './rpt-dtx.component.html',
  styleUrl: './rpt-dtx.component.css',
  standalone: false
})
export class RptDtxComponent {
  _recordService = inject(RecordService);

  @Input() rptDtxData!: WritableSignal<IRptDtx>;
  @Input() rptFromDt: WritableSignal<string>;
  @Input() rptToDt: WritableSignal<string>;
  @Input() rptOptions: WritableSignal<any[]>;
  @Input() seldProvsIDs: WritableSignal<string>;
  @Input() seldFacsIDs: WritableSignal<string>;
  @Input() seldLocalesNo: WritableSignal<string>;
  @Input() sioQuery!: WritableSignal<string | null>;
  @Input() dtxTot: WritableSignal<string>;
  @Input() spinner: WritableSignal<boolean>;

  @ViewChild(MatSort) sort: MatSort;

  sn: string;
  engLang: boolean;
  userID: string;
  userLastNm: string;
  userFirstNm: string;

  modalStack: string[] = [];

  activeView: string = ''; // Holds the currently selected view name
  detail: IRptDtxDet[] = [];
  payment: IRptDtxPay[] = [];
  showDetail: IExpandedCol[] = []; // Array to track which rows have details expanded
  showD: boolean = false; // For *ngIf in template

  dataSource: MatTableDataSource<IRptDtxClm>;
  rptDtxDisplydCols: string[] = [
    'PatLastNm',
    'PatFstNm',
    'caseNo',
    'ins1',
    'totPvdo',
    'totPayed',
    'totAbono',
    'pvdoBal'
  ];

  readonly views = computed<IRptDtxView[]>(() => {
    const arr = this.rptDtxData().views;
    console.log('views computed called', arr.length);
    if (!arr.length) return [];
    return arr.slice();
  });

  readonly parm = computed<IRptDtxParm[]>(() => {
    const arr = this.rptDtxData().parm;
    console.log('parm computed called', arr.length);
    if (!arr.length) return [];
    return arr.slice();
  });

  readonly clm = computed<IRptDtxClm[]>(() => {
    const arr = this.rptDtxData().clm;
    console.log('clm computed called', arr.length);
    if (!arr.length) return [];
    return arr.slice();
  });

  readonly det = computed<IRptDtxDet[]>(() => {
    const arr = this.rptDtxData().det;
    console.log('det computed called', arr.length);
    if (!arr.length) return [];
    return arr.slice();
  });

  readonly pay = computed<IRptDtxPay[]>(() => {
    const arr = this.rptDtxData().pay;
    console.log('pay computed called', arr.length);
    if (!arr.length) return [];
    return arr.slice();
  });

  readonly tot = computed<IRptDtxTot[]>(() => {
    const arr = this.rptDtxData().tot;
    console.log('pay computed called', arr.length);
    if (!arr.length) return [];
    return arr.slice();
  });

  // EFFECT: call `clm()` so changes update dataSource
  // This is declared as a class level field so Angular’s DI is available.
  private readonly _updateData = effect(() => {
    const varr = this.views(); // unwrap computed → views, causes computed to be called
    const parr = this.parm(); // unwrap computed → parm, causes computed to be called
    const carr = this.clm();  // unwrap computed → carr, causes computed to be called
    const darr = this.det();  // unwrap computed → darr, causes computed to be called
    const pyarr = this.pay(); // unwrap computed → pyarr, causes computed to be called
    const tarr = this.tot();  // unwrap computed → tot, causes computed to be called
    this.dataSource = new MatTableDataSource(carr);  // side‐effect: push into MatTableDataSource
  });

  constructor(private _dataMessageService: DataMessageService,
    private _modalService: ModalService,
    private _appToastsService: AppToastsService,
    private _ckRegisterService: CkRegisterService,
    private _xcel: ExportExcelService,
    public _dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this._dataMessageService.currentSnStr.subscribe(snStr => this.snChange(snStr)); // Subscription looking for changes in sn
    this._dataMessageService.currentEngLangStr.subscribe(engLangStr => this.engLangChange(engLangStr)); // Subscription looking for changes in engLang
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort; // Always follow this.dataSource = ... with this to maintain header sort arrow synced
  }

  snChange(snStr: string) { // for when sn changes
    this.sn = snStr;
  }

  engLangChange(engLangStr: string) { // for when language changes
    engLangStr === 'true' ? this.engLang = true : this.engLang = false;
  }

  engDtxDescr(vname): string {
    if (this.engLang) {
      const map = {
        "Cuadre Diario:": "Daily Transactions:",
        " a ": " to ",
        " pedido ": " requested "
      };
      const re = /Cuadre Diario:| a | pedido /g;
      return vname.replace(re, m => map[m]);
    }
    return vname; // Spanish version
  }

  onChange_view(event) {  // Loads up an existing view in event
    this.activeView = event; // Set the active view to the selected one
    this.rptDtxData.update(current => ({
      views: current.views,
      parm: [],
      clm: [],
      det: [],
      pay: [],
      tot: []
    }));
    this.dtxTot.set(''); // Reset dtxTot when changing views
    this.showDetail = []; // Reset showDetail array when changing views

    const qry = "EXEC dbo.spRptDtx @viewNm = '" + this.activeView + "';";
    window.scroll(0, 0);
    console.log('%c' + 'query ' + qry, 'color: green; background: yellow; font-size: 12px');
    this.sioQuery.set(qry);
  }

  onClick_expandDet(cid: string): void {
    // 1) grab the current value
    const current = this.rptDtxData();
    if (!current) return; // Get out if no data

    const idx = current.clm.findIndex(clm => clm.CasID === cid); // Sync clm & idx index

    if (!this.showDetail[idx]?.details.length) {
      this.payment = current.pay.filter(pay => pay.PayCasID === cid).slice(); // Independent payment array for this cid
      this.detail = current.det.filter(det => det.DetCasID === cid).slice(); // Independent det array for this cid
      this.showDetail[idx] = { index: idx, show: true, details: this.detail, payments: this.payment }; // Update showDetail with details and payments
      this.showD = true; // Set show to true when details are expanded

      if (!this.detail.length && !this.payment.length) {  // But if it is an empty case never expand it or you cannot ever collapse it
        this.showDetail[idx] = { index: idx, show: false, details: this.detail, payments: this.payment };
        this.showD = false; // Don't expand if no details or payments
      }
    } else {
      this.payment = []; // Clear payment for this cid
      this.detail = []; // Clear details for this cid
      this.showDetail[idx] = { index: idx, show: false, details: this.detail, payments: this.payment };
      this.showD = false; // Reset show to false when details are collapsed
    }
    // console.log('detail', this.detail);
    // console.log('showDetail', this.showDetail[idx].show);
  }

  isExpanded(row): boolean {  // Call from expanded row in template
    if (!row) return false; // If row is undefined or null, return false

    const current = this.rptDtxData(); // Get the current rptDtxData
    if (!current) return false; // Get out if no data

    const idx = current.clm.findIndex(clm => clm.CasID === row?.CasID); // Current row index

    this.detail = this.showDetail[idx]?.details || [];
    this.payment = this.showDetail[idx]?.payments || [];
    this.showD = this.showDetail[idx]?.show;  // Previously set showD to true or false based on the expanded state in onClick_expandDet(...)

    // console.log('this.showD', this.showD)
    return this.showD;  // Expand (true) or contract (false) details
  }

  open(content, opts, modalNm) {
    this.modalStack.push(modalNm);
    const modal$ = this._modalService.open(content, opts);
    modal$.subscribe({
      next: (result) => {    // Save, Ok, etc
        console.log('result', result);
        const qry = "EXEC dbo.spRptDtx @viewNm = '" + this.activeView + "';";
        window.scroll(0, 0);
        console.log('%c' + 'query ' + qry, 'color: green; background: yellow; font-size: 12px');
        this.sioQuery.set(qry);
        this.modalStack.pop();
      },
      error: (reason) => {    // dismiss, X, Close, Esc
        console.log('reason', reason);
        console.log('modalStack', this.modalStack);
        switch (this.modalStack[this.modalStack.length - 1]) {
          case 'recordCaseModal':
            // Do some cleanup
            break;
          default:
        }
        this.modalStack.pop();
      }
    });
  }

  onClick_menu(event) {
    if (this.spinner()) {
      this._appToastsService.updateDeadCenter(true); // Show toast in dead center of viewport
      this._appToastsService.show(this.engLang ? 'Wait for the current task to finish' : 'Espere que la tarea activa termine', { header: this.engLang ? 'Busy' : 'Ocupado', autohide: true, warning: true });
      return; // Get out if spinner is already set to true     
    }

    switch (event.target.id) {
      case 'expandRowsDown':
        this.rptDtxData().clm.forEach((clm) => {
          if (!this.isExpanded(clm)) {
            this.onClick_expandDet(clm.CasID);
          }
        });
        break;
      case 'collapseRowsUp':
        this.rptDtxData().clm.forEach((clm) => {
          if (this.isExpanded(clm)) {
            this.onClick_expandDet(clm.CasID);
          }
        });
        break;
      case 'print':
        this.printRpt();
        break;
      case 'delete':
        this.deleteRpt();
        break;
      default:
    }
  }

  makeNum(value: number | string): number {
    return Number(value.toString().replace(/,/g, '')); // Convert string with commas to number
  }

  removeLocaleLeadZeros(locale: string): string {
    // Remove leading zeros from the locale string
    return locale.replace(/\-0*/, '-');
  }

  truncateString(str: string, len: number): string {
    if (str === '(0)') return ''; // If the string is '(0)', supress it
    // Truncate the string to the specified length and add ellipsis if necessary
    return str.length > len ? str.substring(0, 10) + '...' + str.substring(str.length - 7) : str;
  }

  printRpt() {
    if (this.rptDtxData().clm.length === 0) {
      this._appToastsService.updateDeadCenter(false); // Show toast in dead center of viewport
      this._appToastsService.show(this.engLang ? 'No data to print' : 'No hay datos para imprimir', { header: this.engLang ? 'Error preparing report' : 'Error preparando reporte', autohide: true, warning: true });
      return; // Get out if no data to print
    }

    this.spinner.set(true); // Show spinner while preparing report
    let pgCnt = 0;  // Page number
    let lc = 0; // Line count
    let clms: any[] = []; // Array to hold clm data

    this.rptDtxData().clm.forEach((clm, cindex) => {
      let i = 0;  // Individual case lines

      let det: any[] = [];
      let detFiltered = this.rptDtxData().det.filter(d => d.DetCasID === clm.CasID);
      detFiltered.forEach((d) => {
        det.push({
          DetPS: d.DetPS,
          detPcodeCol: d.detPcodeCol,
          detXpect1: d.detXpect1,
          detXpect2: d.detXpect2,
          detPvdo: d.detPvdo,
          detQty: d.detQty,
          DetDesc: d.detDesc
        })
      });

      let pay: any[] = [];
      let payFiltered = this.rptDtxData().pay.filter(p => p.PayCasID === clm.CasID);
      payFiltered.forEach((p) => {
        pay.push({
          payAmnt: p.payAmnt,
          payPS: p.payPS,
          payMode: p.payMode,
          payCkNo: p.payCkNo,
          payMemo: p.payMemo
        });
      });

      let imx = det.length >= pay.length ? det.length : pay.length; // Use the larger length between det and pay lengths
      while (i < imx) { // Loop through the maximum length of det or pay lines
        clms.push({
          CasID: i === 0 ? clm.CasID : '',
          patFullNm: i === 0 ? clm.PatLastNm + ',' + clm.PatFstNm : '',
          ins1: i === 0 ? clm.ins1 : '',
          ins2: i === 0 ? (clm.ins2 === '(0)' ? '' : clm.ins2) : '',
          totXpect: i === 0 ? +clm.totXpect1.replace(/\,*/, '') + +clm.totXpect2.replace(/\,*/, '') : '',
          totPvdo: i === 0 ? clm.totPvdo : '',
          totPayed: i === 0 ? clm.totPayed : '',
          totAbono: i === 0 ? clm.totAbono : '',
          pvdoBal: i === 0 ? clm.pvdoBal : '',
          caseNo: i === 0 ? clm.caseNo.replace(/\-0*/g, '-') : '',

          DetPS: det[i]?.DetPS ? det[i]?.DetPS : '',
          detPcodeCol: det[i]?.detPcodeCol ? det[i]?.detPcodeCol : '',
          detXpect1: det[i]?.detXpect1 ? det[i]?.detXpect1 : '',
          detXpect2: det[i]?.detXpect2 ? det[i]?.detXpect2 : '',
          detPvdo: det[i]?.detPvdo ? det[i]?.detPvdo : '',
          detQty: det[i]?.detQty ? det[i]?.detQty : '',
          DetDesc: det[i]?.detDesc ? det[i]?.detDesc : '',

          payAmnt: pay[i]?.payAmnt ? pay[i].payAmnt : '',
          payPS: pay[i]?.payPS ? pay[i]?.payPS : '',
          payMode: pay[i]?.payMode ? pay[i]?.payMode : '',
          payCkNo: pay[i]?.payCkNo ? pay[i].payCkNo : '',
          payMemo: pay[i]?.payMemo ? pay[i].payMemo : '',

          "break": 0,
          "page": pgCnt, // Set page to the current page count
          "pages": 0, // Placeholder for total pages, will be set later
          "index": cindex,
        });

        if (lc % 20 === 0) { // 20 lines per page
          clms[lc].break = 1; // Set break flag for the current record to force a page break
          pgCnt++; // Increment page count
        }
        // console.log('cindex', cindex);
        i++;
        lc++;
      }
    });

    clms.forEach(clm => {
      clm.pages = pgCnt; // Set total pages for each record
    });

    let data: any = {
      "head": {
        "site": {
          "nm": this._recordService.site.nm,
          "ad1": this._recordService.site.ad1,
          "ad2": this._recordService.site.ad2,
          "ct": this._recordService.site.ct,
          "st": this._recordService.site.st,
          "zp": this._recordService.site.zp,
          "tl1": this._recordService.site.tl1,
          "xt1": this._recordService.site.xt1,
          "tl2": this._recordService.site.tl2,
          "xt2": this._recordService.site.xt2
        },
        "sn": this._recordService.sn,
        "dt": moment(new Date()).format('MM/DD/YY h:mm a'),
        "title": this.engLang ? this.engDtxDescr(this.activeView) : this.activeView,
        "subTitNm": this.engLang ? 'Last Name, First' : 'Apellidos, Nombre',
        "subTitIns": this.engLang ? 'Plans 1/2' : 'Planes 1/2',
        "subTitXpect": this.engLang ? 'Expected' : 'Esperado',
        "subTitPvdo": this.engLang ? 'Patient' : 'Privado',
        "subTitPayed": this.engLang ? 'Paid' : 'Pagado',
        "subTitAbono": this.engLang ? 'Partial' : 'Abono',
        "subTitCase": this.engLang ? 'Claim' : 'Caso',
        "subTitCpt": 'CPT',
        "subTitIns1": 'Plan 1',
        "subTitIns2": 'Plan 2',
        "subTitPvdoDet": this.engLang ? 'Patient' : 'Privado',
        "subTitPvdoQty": this.engLang ? 'Qty' : 'Cant',
        "subTitPayedDet": this.engLang ? 'Paid' : 'Pagado',
        "subTitMode": this.engLang ? 'Mode' : 'Modo',
        "subTitCkNo": 'Ck No.',
        "subTitMemo": 'Memo',
      },
      "clms": clms,
      "param": {
        "parmFromDt": this.rptDtxData().parm[0].parmFromDt,
        "parmToDt": this.rptDtxData().parm[0].parmToDt,
        "parmProv": this.rptDtxData().parm[0].parmProv,
        "parmFac": this.rptDtxData().parm[0].parmFac,
        "parmLocale": this.rptDtxData().parm[0].parmLocale,
        "parmUser": this.rptDtxData().parm[0].parmUser,
        "parmOHA": this.rptDtxData().parm[0].parmOHA === 'O' ? (this.engLang ? 'Office claims only' : 'Solo casos oficina') : (this.rptDtxData().parm[0].parmOHA === 'H' ? (this.engLang ? 'Exclude office claims' : 'Excluir casos oficina') : ''),
      },
      "opt": {},
      "totTits": {
        payedTot: this.engLang ? 'Register' : 'Caja',
        insXpectTot: this.engLang ? 'Insurance' : 'Seguros',
        pvdoTot: this.engLang ? 'Patients' : 'Privado',
        abonoTot: this.engLang ? 'Instalment' : 'Abono',
        cashTot: 'Cash',
        ckTot: 'Cks',
        athTot: 'ATH',
        ccTot: this.engLang ? 'Credit Card' : 'Tarjetas',
        corrTot: this.engLang ? 'Correction' : 'Correcciones',
        otherTot: this.engLang ? 'Other' : 'Otros',
        clmsCnt: this.engLang ? 'Claims' : 'Casos'
      },
      "tots": {
        payedTot: this.rptDtxData().tot[0].payedTot,
        insXpectTot: this.rptDtxData().tot[0].insXpectTot,
        pvdoTot: this.rptDtxData().tot[0].pvdoTot,
        abonoTot: this.rptDtxData().tot[0].abonoTot,
        cashTot: this.rptDtxData().tot[0].cashTot,
        ckTot: this.rptDtxData().tot[0].ckTot,
        athTot: this.rptDtxData().tot[0].athTot,
        ccTot: this.rptDtxData().tot[0].ccTot,
        corrTot: this.rptDtxData().tot[0].corrTot,
        otherTot: this.rptDtxData().tot[0].othTot,
        clmsCnt: this.rptDtxData().clm.length
      },
      "lastPg": pgCnt
    };

    // console.log('printRpt data', data);
    this.buildRpt(data);
  }

  renderBlob(blob: Blob) {
    const url = window.URL.createObjectURL(blob);
    const newTab = window.open(url);

    if (newTab) {
      newTab.onload = () => {
        window.URL.revokeObjectURL(url);
      };
    } else {
      this._appToastsService.updateDeadCenter(false); // Show toast in dead center of viewport - property sent back to app.component via @ViewChild in app.component
      this._appToastsService.show('Failed to open new tab in browser.', { header: 'Error preparing report * Error preparando reporte', autohide: false, error: true });
    }
    this.spinner.set(false);
  }

  backFromServerError(error): void {
    this._appToastsService.updateDeadCenter(false); // Show toast in dead center of viewport - property sent back to app.component via @ViewChild in app.component
    this._appToastsService.show(error.message, { header: 'Error preparing report * Error preparando reporte', autohide: false, error: true });
  }

  buildRpt(data: any) {
    const bdy = {
      "template":
      {
        "name": "/rpts/dtx/dtx-html"
      }, "data": data,
      "options": { "reports": { "save": true } }
    };

    console.log('printData', data);

    this._ckRegisterService.postRenderJsRpt(bdy).subscribe({
      next: blob => { this.renderBlob(blob) },
      error: (error: any) => { this.backFromServerError(error) }
    });
  }

  deleteRpt() {
    if (!this.rptDtxData().views.find(v => v.name === this.activeView)) { // Probably already deleted
      this.activeView = '';
    }

    if (!this.activeView) {
      this._appToastsService.updateDeadCenter(true); // Show toast in dead center of viewport
      this._appToastsService.show(this.engLang ? 'Select the desired report to delete' : 'Seleccione el reporte deseado para eliminar', { header: this.engLang ? 'Select report' : 'Seleccione reporte', autohide: true, warning: true });
      return;
    }

    // Prompt to delete
    this._modalService.confirm(this.engLang ? 'Delete selected report?' : '¿Eliminar el reporte seleccionado?'
      , this.engLang ? 'Confirm' : 'Confirme'
      , true
      , true
      , 'Ok'
      , 'Cancel')
      .pipe(
        take(1) // take() manages unsubscription for us
      ).subscribe(response => {
        if (response) { // response = undefined if Esc was pressed, False if closed with upper rt hand x
          this.spinner.set(true);
          const qry = "EXEC dbo.spRptDtxDelete @viewNm = '" + this.activeView + "';";
          window.scroll(0, 0);
          console.log('%c' + 'query ' + qry, 'color: green; background: yellow; font-size: 12px');
          this.sioQuery.set(qry);
        }
      });
  }

}

